import { useMemo, useState } from 'react'
import Seo, { softwareAppSchema } from '../components/Seo'
import Breadcrumbs from '../components/Breadcrumbs'
import Faq from '../components/Faq'
import type { QA } from '../components/Faq'
import ResultSheet from '../components/ResultSheet'
import CalcActions from '../components/CalcActions'
import InfoTip from '../components/InfoTip'
import { Field, SmartField, Segmented } from '../components/Inputs'
import { computeLumber, DEFAULT_LUMBER } from '../calculators/lumber'
import type { LumberInputs, StudSpacing, StudSize } from '../calculators/lumber'
import { calcsInCategory } from '../data/calculators'
import { saveSheet, genId } from '../lib/storage'
import { useLocale, localizedHref } from '../i18n/locale'
import { t } from '../i18n/strings'
import { calcName, categoryName } from '../i18n/calculators'

const PATH = '/calculators/lumber/'
const CATEGORY = 'paint-and-walls'

const TITLE = 'Lumber & Framing Calculator — Stud Count, Plates & Headers | RenoSheets'
const DESC = 'Free lumber framing calculator. Enter wall length, height, and stud spacing to get exact stud count, top + bottom plates, headers and framing nails. 16" or 24" OC, 2×4 or 2×6. No signup.'

const FAQS: QA[] = [
  { q: 'How many studs do I need for a 12 ft wall?', a: 'A 12 ft wall at 16 in on-centre (OC) spacing needs 10 studs: one stud every 16 inches (12 ft × 12 in ÷ 16 in = 9 spacings), plus one extra stud to cap the end. Add 3 more studs per door or window opening for the king + jacks, plus 10% waste for cuts and mistakes.' },
  { q: 'What is the difference between 16 and 24 inches on centre?', a: 'On-centre (OC) spacing is the distance between the centerlines of adjacent studs. 16 in OC is standard for most residential interior and exterior walls in North America — it uses more lumber but is stronger and gives drywall a closer fastening grid. 24 in OC uses about 33% less lumber but requires thicker drywall (5/8 in) and is mostly used in single-storey light-load walls and energy-efficient framing (Advanced Framing).' },
  { q: 'How long are the studs in a standard 8-foot wall?', a: 'Pre-cut studs are 92⅝ inches (7 ft 8⅝ in) long. Combined with one 1.5 in bottom plate and a 3 in double top plate (1.5 in × 2), the finished wall is exactly 97⅛ in (about 8 ft 1⅛ in). If you cut your own studs to 92⅝ in or 93 in, the math works out the same.' },
  { q: 'What size of header do I need over a door or window?', a: 'For a non-load-bearing interior wall, a single flat 2×4 across the opening is enough. For load-bearing walls, the rule of thumb is one inch of 2× header depth per foot of span — so a 3 ft opening uses a 2×6 header (5.5 in deep), a 6 ft opening uses a 2×8 header, etc. Always defer to your local building code or engineer for load-bearing decisions.' },
  { q: 'How many 16d nails do I need to frame a wall?', a: 'Plan on roughly 1 lb of 16d framing nails per 6-7 studs. A typical 12 ft wall with 10 studs uses about 1.5 lb of 16d sinkers or commons for the stud-to-plate connections, plus a similar amount of 8d nails for plate-to-floor and sole plate-to-stud toe-nailing.' },
  { q: 'Should I use 2×4 or 2×6 lumber for framing?', a: '2×4 is standard for interior walls and most exterior walls in regions with milder climates. 2×6 is used for exterior walls in cold climates because the 5.5 in cavity holds more insulation (R-19+ vs R-13 for 2×4), and for plumbing walls behind bathroom fixtures to fit drain stacks. Always 2×6 for any wall framing a soaker tub.' },
]

export default function LumberCalculator() {
  const locale = useLocale()
  const L = (en: string) => localizedHref(en, locale)
  const tr = (k: string) => t(locale, k)

  const [i, setI] = useState<LumberInputs>(DEFAULT_LUMBER)
  const [saved, setSaved] = useState(false)
  const r = useMemo(() => computeLumber(i, locale), [i, locale])
  const set = (patch: Partial<LumberInputs>) => {
    setI((p) => ({ ...p, ...patch }))
    setSaved(false)
  }

  const onSave = async () => {
    await saveSheet({
      id: genId(),
      type: 'lumber',
      name: `Lumber — ${i.wallLengthFt} ft wall · ${i.studSize} ${i.spacing}" OC`,
      createdAt: new Date().toISOString(),
      summary: `${r.studCount} studs · ${r.plateCount} plates · ${r.totalBoardFt} bd ft`,
      lines: r.lines.filter((l) => l.kind === 'consumable').map((l) => ({ label: l.label, qty: l.qty, unit: l.unit })),
    })
    setSaved(true)
  }

  const related = calcsInCategory(CATEGORY).filter((x) => x.slug !== 'lumber')
  const assumptions = `${i.wallLengthFt} ft × ${i.wallHeightFt} ft wall · ${i.studSize} at ${i.spacing} in OC · ${i.doors} door + ${i.windows} window · ${i.wastePct}% waste`

  // Step-by-step "How we got this" content for the ⓘ button on the result card.
  const buildExplain = (): React.ReactNode => {
    const baseStuds = i.wallLengthFt > 0 ? Math.ceil((i.wallLengthFt * 12) / i.spacing) + 1 : 0
    const openings = i.doors + i.windows
    const openingStuds = openings * 3
    return (
      <ol className="list-decimal space-y-1 pl-5">
        <li>
          {locale === 'fr'
            ? `Montants de base = arrondi.sup(${i.wallLengthFt} × 12 ÷ ${i.spacing}) + 1`
            : `Base studs = ceil(${i.wallLengthFt} × 12 ÷ ${i.spacing}) + 1`}
          : <strong>{baseStuds}</strong>
        </li>
        <li>
          {locale === 'fr'
            ? `+ Ouvertures (${i.doors} porte + ${i.windows} fenêtre) × 3 montants chacune`
            : `+ Openings (${i.doors} door + ${i.windows} window) × 3 studs each`}
          : <strong>+{openingStuds}</strong>
        </li>
        <li>
          {locale === 'fr' ? `+ ${i.wastePct} % de perte` : `+ ${i.wastePct}% waste`}
          : <strong>{r.studCount} studs</strong>
        </li>
        <li>
          {locale === 'fr' ? `Longueur de coupe par montant = ${i.wallHeightFt} pi − 4,5 po (lisse haute double + basse)` : `Stud cut length = ${i.wallHeightFt} ft − 4.5 in (double top + bottom plate)`}
          : <strong>{r.studLengthIn} in</strong>
        </li>
        <li>
          {locale === 'fr' ? `Achetez à ${r.studPurchaseLengthFt} pi (stock le plus proche)` : `Buy at ${r.studPurchaseLengthFt} ft (nearest stock length)`}
        </li>
        <li>
          {locale === 'fr'
            ? `Lisses = ${i.wallLengthFt} pi × 3 (double haute + simple basse) = ${r.plateLinearFt} pi linéaires`
            : `Plates = ${i.wallLengthFt} ft × 3 (double top + single bottom) = ${r.plateLinearFt} lin ft`}
        </li>
        <li>
          {locale === 'fr'
            ? `÷ ${r.plateLengthFt} pi par pièce + ${i.wastePct} % de perte`
            : `÷ ${r.plateLengthFt} ft per piece + ${i.wastePct}% waste`}
          : <strong>{r.plateCount} plates</strong>
        </li>
        {r.headerCount > 0 && (
          <li>
            {locale === 'fr'
              ? `Linteaux = ${r.headerCount} × ~4 pi de 2×6 pour les ouvertures`
              : `Headers = ${r.headerCount} × ~4 ft of 2×6 for openings`}
          </li>
        )}
        <li>
          {locale === 'fr'
            ? `Clous = montants × 0,15 lb/montant, arrondi`
            : `Nails = studs × 0.15 lb/stud, rounded up`}
          : <strong>{r.nailLbs} lb</strong>
        </li>
      </ol>
    )
  }

  return (
    <div className="mx-auto max-w-6xl px-4 py-6">
      <Seo title={TITLE} description={DESC} path={PATH} schema={softwareAppSchema(calcName('lumber', 'Lumber & Framing Calculator', locale), DESC, localizedHref(PATH, locale))} />
      <Breadcrumbs
        items={[
          { name: tr('crumb.home'), href: L('/') },
          { name: categoryName(CATEGORY, 'Paint & Walls', locale), href: L(`/calculators/${CATEGORY}/`) },
          { name: calcName('lumber', 'Lumber & Framing Calculator', locale), href: L(PATH) },
        ]}
      />

      <h1 className="text-3xl">Lumber & Framing Calculator</h1>
      <p className="mt-1 max-w-content text-text-muted">Enter your wall dimensions and stud spacing for an exact framing buy list — studs, top + bottom plates, headers and nails.</p>

      <div className="mt-6 grid gap-6 lg:grid-cols-5">
        <form className="rounded-xl border border-border bg-surface p-5 lg:col-span-3" onSubmit={(e) => e.preventDefault()}>
          <div className="rounded-md border border-border p-3">
            <p className="mb-1 text-sm font-medium text-text-body">
              Wall dimensions
              <InfoTip text="Length is wall-to-wall. Height is finished floor to finished ceiling. The calculator subtracts 4.5 in for the bottom plate + double top plate when sizing studs." />
            </p>
            <div className="grid grid-cols-2 gap-2">
              <SmartField label="Length" imperialValue={i.wallLengthFt} onChangeImperial={(n) => set({ wallLengthFt: n })} imperialUnit="ft" step={0.5} />
              <SmartField label="Height" imperialValue={i.wallHeightFt} onChangeImperial={(n) => set({ wallHeightFt: n })} imperialUnit="ft" step={0.5} />
            </div>
          </div>

          <div className="mt-4 flex flex-wrap gap-6">
            <Segmented
              label="Stud spacing"
              value={String(i.spacing) as '16' | '24'}
              onChange={(v) => set({ spacing: Number(v) as StudSpacing })}
              options={[{ v: '16', l: '16" OC' }, { v: '24', l: '24" OC' }]}
            />
            <Segmented
              label="Stud size"
              value={i.studSize}
              onChange={(v) => set({ studSize: v as StudSize })}
              options={[{ v: '2x4', l: '2×4' }, { v: '2x6', l: '2×6' }]}
            />
          </div>

          <div className="mt-4 grid grid-cols-2 gap-3">
            <Field label="Door openings" value={i.doors} onChange={(n) => set({ doors: n })} min={0} step={1} />
            <Field label="Window openings" value={i.windows} onChange={(n) => set({ windows: n })} min={0} step={1} />
          </div>

          <div className="mt-5 rounded-lg bg-surface-2 p-3">
            <div className="flex items-center justify-between text-sm">
              <span className="flex items-center font-medium text-text-body">
                {tr('calc.wasteAllowance')}
                <InfoTip text="Extra studs for cuts, mistakes, and the inevitable warped board you'll cull at the lumber yard. 10% is standard for straight walls; bump to 15% for walls with lots of openings or angled corners." />
              </span>
              <span className="tnum text-brand-text">{i.wastePct}%</span>
            </div>
            <input type="range" min={0} max={25} step={1} value={i.wastePct} onChange={(e) => set({ wastePct: parseInt(e.target.value) })} className="mt-1 w-full accent-brand" />
          </div>

          <div className="mt-5 rounded-lg bg-surface-2 p-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-text-muted">Wall summary</p>
            <ul className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
              <li>Studs: <strong className="tnum">{r.studCount} × {r.studPurchaseLengthFt} ft</strong></li>
              <li>Stud cut: <strong className="tnum">{r.studLengthIn} in</strong></li>
              <li>Plates: <strong className="tnum">{r.plateCount} × {r.plateLengthFt} ft</strong></li>
              <li>Plate lin ft: <strong className="tnum">{r.plateLinearFt} ft</strong></li>
              <li>Headers: <strong className="tnum">{r.headerCount}</strong></li>
              <li>Total board ft: <strong className="tnum">{r.totalBoardFt}</strong></li>
            </ul>
          </div>

          <CalcActions onSave={onSave} saved={saved} locale={locale} className="mt-6 border-t border-border pt-4" />
        </form>

        <div className="lg:col-span-2">
          <ResultSheet
            primary={{ name: 'Framing studs', label: `${i.studSize} at ${i.spacing}" OC, ${r.studPurchaseLengthFt} ft each`, qty: r.studCount, unit: 'piece', query: `${i.studSize} stud framing lumber` }}
            secondary={{ name: 'Plates', label: `${PLATE_LABEL[i.studSize]}, ${r.plateLengthFt} ft`, qty: r.plateCount, unit: 'piece', query: `${i.studSize} plate framing lumber` }}
            lines={r.lines}
            assumptions={assumptions}
            explain={buildExplain()}
          />
        </div>
      </div>

      <article className="mx-auto mt-8 max-w-content text-text-body">
        <h2 className="text-2xl">{locale === 'fr' ? 'Ce qui influence vraiment la quantité de bois nécessaire' : 'What actually affects how much lumber you need'}</h2>
        <p className="mt-3">
          {locale === 'fr'
            ? 'L’espacement des montants est le facteur le plus déterminant sur la quantité de bois, et c’est une décision de code, pas un choix esthétique. 16 pouces centre à centre est le standard pour la plupart des murs porteurs, mais un espacement de 24 pouces est permis dans plusieurs juridictions pour les murs non porteurs lorsqu’il est combiné à un gypse plus épais ou du bois d’ingénierie — cela seul peut réduire le nombre de montants d’un tiers. Chaque ouverture brute (porte ou fenêtre) ajoute un coût fixe au-delà des montants qu’elle déplace : un montant royal, des montants nains pour soutenir le linteau, et des montants sablières au-dessus ou en dessous, alors un mur avec plusieurs fenêtres a besoin de nettement plus de bois que la même longueur de mur plein. La taille du linteau est aussi déterminée par la portée, pas la préférence — une courte porte intérieure a besoin d’un linteau beaucoup plus léger qu’une large ouverture de fenêtre sur un mur porteur, et utiliser un linteau sous-dimensionné est un problème structurel, pas seulement une perte d’argent. Acheter du bois précoupé à la longueur des montants (92-5/8 po pour des murs de 8 pi) épargne du temps de coupe par rapport à acheter du bois standard de 8 pi ou 10 pi et à couper chaque pièce.'
            : 'Stud spacing is the single biggest lever on lumber count, and it\'s a code decision, not a style choice. 16 inches on-center is the default for most load-bearing walls, but 24-inch spacing is permitted in many jurisdictions for non-load-bearing walls when paired with thicker drywall or engineered lumber — that alone can cut stud count by a third. Every rough opening (door or window) adds a fixed cost beyond the studs it displaces: a king stud, jack studs to support the header, and cripple studs above or below, so a wall with several windows needs meaningfully more lumber than the same length of blank wall. Header size is also driven by span, not preference — a short interior doorway needs a much lighter header than a wide window opening on a load-bearing wall, and using an undersized header is a structural problem, not just a waste-of-money one. Buying stud-length precut lumber (92-5/8 in for 8 ft walls) saves cutting time versus buying full 8 ft or 10 ft stock and trimming every piece.'}
        </p>
        <h2 className="mt-8 text-2xl">How to calculate framing lumber</h2>
        <p className="mt-3">Wall framing math is mostly counting. Stud count is the dominant variable; everything else scales off it:</p>
        <pre className="mt-3 overflow-x-auto rounded-lg bg-surface-2 p-3 text-sm text-text">{`studs   = ceil(wall length in inches ÷ spacing) + 1
        + 3 × openings (king + 2 jacks/cripples per opening)
        × (1 + waste%)

plates  = wall length × 3 linear feet  (double top + single bottom)
        ÷ stock length (typically 12 ft) → number of pieces

headers = 1 per opening × ~4 ft of 2×6 (load-bearing walls only)

nails   = studs × ~0.15 lb (16d sinkers / commons)`}</pre>
        <p className="mt-3">Standard pre-cut studs are 92⅝" long. With a 1½" bottom plate and a 3" double top plate, that gives a finished wall height of 97⅛" (about 8 ft 1⅛"). For walls taller than 8 ft, buy 10-ft or 12-ft stock and rip to length.</p>

        <h3 className="mt-6 text-xl">Common mistakes</h3>
        <ul className="mt-2 list-disc space-y-1 pl-5">
          <li><strong>Forgetting the extra end stud.</strong> The formula <code>length ÷ spacing</code> gives you the number of <em>spaces</em>, not studs. You need one more stud than spaces to cap the end of the wall.</li>
          <li><strong>Underestimating opening studs.</strong> Every door or window needs a king stud + 2 jack/trimmer studs at minimum. Wider openings or doubled headers need cripples and additional jacks.</li>
          <li><strong>Skipping plate-to-floor anchors.</strong> The bottom plate is bolted to a concrete slab with anchor bolts every 4–6 ft (check local code), not just nailed.</li>
          <li><strong>2×4 vs 2×6 mismatch.</strong> Your plates must match your studs — a 2×6 wall uses 2×6 plates, not 2×4. Mixing them is a code violation and a thermal bridge.</li>
        </ul>
      </article>

      <Faq items={FAQS} />

      <section className="mt-10">
        <h2 className="text-2xl">Related calculators</h2>
        <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3">
          {related.map((x) => (
            <a
              key={x.slug}
              href={L(`/calculators/${x.slug}/`)}
              className={`rounded-lg border p-3 text-sm transition-colors ${
                x.live
                  ? 'border-border bg-surface-2 font-medium text-brand-text hover:border-brand hover:bg-surface'
                  : 'border-dashed border-border bg-surface-2 text-text-muted'
              }`}
            >
              {calcName(x.slug, x.name, locale)} {x.live ? '' : <span className="text-text-subtle">{tr('calc.soon')}</span>}
            </a>
          ))}
        </div>
      </section>

      <div className="mx-auto mt-8 max-w-content border-t border-border pt-4 text-xs text-text-subtle">
        Reviewed by the RenoSheets team for calculation accuracy. Method: stud count via ceil(length ÷ spacing) + 1 + 3 per opening + waste; plates × 3 (top×2 + bottom×1); headers conservative at ~4 ft per opening; framing nails 0.15 lb/stud. Last updated 2026-06-04. Estimates only — verify against your framing plan and local building code before purchasing.
      </div>
    </div>
  )
}

// Inline plate-label helper — keeps the secondary result line tidy without
// polluting the lumber.ts compute module.
const PLATE_LABEL: Record<'2x4' | '2x6', string> = {
  '2x4': 'top + bottom plate stock',
  '2x6': 'top + bottom plate stock',
}
